import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import type { ListenerEffectAPI, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import PricingApi from '@/api/PricingApi';
import CreatorProjectsApi from '@/api/CreatorProjectsApi';
import { creatorActions } from '@/store/slices/creatorSlice';
import canonicalizeConfiguration, { preferUuidConfiguration } from '@/utils/configuration';
import { isCreatorProjectLockedError } from '@/utils/creatorProjectErrors';
import {
	PERSONALIZATION_NOTES_CONFIGURATION_KEY,
	normalizePersonalizationNotes,
} from '@/utils/personalizationNotes';
import { buildCreatorProjectPreviewUrl } from '@/utils/url';
import type { RootState } from '@/store/types';

type AppDispatch = ThunkDispatch<RootState, unknown, UnknownAction>;
import {
	clearCreatorSnapshot,
	saveCreatorSnapshot,
} from '@/store/persistence/creatorStorage';
import { CreatorSnapshot } from '@/types/creator';

const buildSnapshot = (
	state: RootState['creator'],
	savedAt: number
): CreatorSnapshot => ({
	selectedProductTypeId: state.selectedProductTypeId,
	selectedProductBoxStyle: state.selectedProductBoxStyle,
	selectedVariantId: state.selectedVariantId,
	configuration: state.configuration,
	options: {
		currentStep: state.options.currentStep,
		viewType: state.options.viewType,
		layerType: state.options.layerType,
		isSideView: state.options.isSideView,
		selectedDecor: state.options.selectedDecor,
		isFullPreviewOpened: state.options.isFullPreviewOpened,
		activeSurfaceId: state.options.activeSurfaceId,
		activeSurfaceAxes: state.options.activeSurfaceAxes,
		hoverSurfaceId: state.options.hoverSurfaceId,
		surfaceViewMode: state.options.surfaceViewMode,
		selectedElement: state.options.selectedElement,
		personalizationMode: state.options.personalizationMode,
	},
	pricing: state.pricing.breakdown,
	textElements: state.textElements,
	imageElements: state.imageElements,
	uploads: state.uploads.map((upload) => ({ ...upload })),
	savedAt,
	apiProjectId: state.apiProjectId,
	isTemplateEditor: state.isTemplateEditor,
	templateRequestId: state.templateRequestId,
	isViewOnly: state.isViewOnly,
});

export const creatorListenerMiddleware = createListenerMiddleware<RootState, AppDispatch>();


const shouldHandleAction = isAnyOf(
	creatorActions.setConfigurationValue,
	creatorActions.setConfiguration,
	creatorActions.setSelectedVariant,
	creatorActions.consumePresetAttributes,
	creatorActions.setSelectedProductType,
	creatorActions.setSelectedProductBoxStyle,
	creatorActions.setStage,
	creatorActions.setCreatorStep,
	creatorActions.updateOptions,
	creatorActions.setTemplateEditorMode,
	creatorActions.resetCreator,
	creatorActions.resetCurrentProject,
	creatorActions.setTextElements,
	creatorActions.addTextElement,
	creatorActions.updateTextElement,
	creatorActions.removeTextElement,
	creatorActions.restoreTextElement,
	creatorActions.moveSurfaceElementLayer,
	creatorActions.setImageElements,
	creatorActions.addImageElement,
	creatorActions.updateImageElement,
	creatorActions.removeImageElement,
	creatorActions.restoreImageElement,
	creatorActions.addUploadFile,
	creatorActions.removeUploadFile,
	creatorActions.clearUploadFiles
);

creatorListenerMiddleware.startListening({
	predicate: (action) => shouldHandleAction(action),
	effect: async (
		_: unknown,
		listenerApi: ListenerEffectAPI<RootState, AppDispatch>
	) => {
		listenerApi.cancelActiveListeners();
		listenerApi.dispatch(creatorActions.markAutosaveChange(Date.now()));

		const { creator: initialCreator } = listenerApi.getState() as RootState;
		const autosaveDelay = Math.max(200, initialCreator.settings.autosaveInterval);

		await listenerApi.delay(autosaveDelay);

		const { creator } = listenerApi.getState() as RootState;

		if (creator.isViewOnly) {
			return;
		}

		if (!creator.selectedProductTypeId) {
			clearCreatorSnapshot();
			listenerApi.dispatch(creatorActions.resetAutosaveMeta());
		} else {
			const savedAt = Date.now();
			const snapshot = buildSnapshot(creator, savedAt);
			saveCreatorSnapshot(snapshot);
			listenerApi.dispatch(creatorActions.markAutosaveSaved(savedAt));

			if (creator.apiProjectId) {
				const configurationPayload = preferUuidConfiguration(
					(snapshot.configuration ?? creator.configuration) || {}
				);
				const personalizationNotes = normalizePersonalizationNotes(
					configurationPayload[PERSONALIZATION_NOTES_CONFIGURATION_KEY] ??
						creator.configuration[PERSONALIZATION_NOTES_CONFIGURATION_KEY]
				);
				const syncPayload = {
					productTypeId: creator.selectedProductTypeId,
					configuration: configurationPayload,
					personalization: {
						textElements: snapshot.textElements ?? creator.textElements,
						imageElements: snapshot.imageElements ?? creator.imageElements,
						options: creator.options,
						notes: personalizationNotes || null,
					},
					uploads: (snapshot.uploads ?? creator.uploads).map((upload) => ({
						id: upload.remoteId,
						name: upload.name,
						size: upload.size,
					})),
					metadata: {
						variantId: creator.selectedVariantId,
						personalizationNotes: personalizationNotes || null,
						previewUrl: creator.apiProjectId
							? buildCreatorProjectPreviewUrl(creator.apiProjectId)
							: '',
					},
				};
				try {
					await CreatorProjectsApi.update(creator.apiProjectId, syncPayload);
				} catch (error) {
					if (isCreatorProjectLockedError(error)) {
						try {
							const created = await CreatorProjectsApi.create({
								...syncPayload,
								status: 'draft',
							});
							listenerApi.dispatch(creatorActions.setApiProjectId(created.id));
						} catch (createError) {
							if (import.meta.env.DEV) {
								console.error('Creator project re-create failed', createError);
							}
						}
					} else if (import.meta.env.DEV) {
						console.error('Creator project sync failed', error);
					}
				}
			}
		}

			const { selectedProductTypeId } = creator;
			// Używaj konfiguracji zsynchronizowanej po functionalId
			const configuration = creator.configuration ?? {};
			const normalizedConfiguration = canonicalizeConfiguration(configuration);
			const hasVariantSelection = Boolean(creator.selectedVariantId);

		if (!selectedProductTypeId) {
			listenerApi.dispatch(creatorActions.setPricingBreakdown(null));
			listenerApi.dispatch(creatorActions.setPricingStatus('idle'));
			return;
		}

		if (!hasVariantSelection) {
				if (!normalizedConfiguration || Object.keys(normalizedConfiguration).length === 0) {
					listenerApi.dispatch(creatorActions.setPricingBreakdown(null));
					listenerApi.dispatch(creatorActions.setPricingStatus('idle'));
					return;
				}
			}

		const sizeMode = normalizedConfiguration['sizeMode'];
		const isCustomSize = String(sizeMode ?? '') === 'custom';

			if (isCustomSize && !hasVariantSelection) {
				listenerApi.dispatch(creatorActions.setPricingBreakdown(null));
				listenerApi.dispatch(creatorActions.setPricingStatus('idle'));
				return;
			}

			listenerApi.dispatch(creatorActions.setPricingStatus('loading'));

		try {
			const result = await PricingApi.calculateQuote(
				{
					productTypeId: selectedProductTypeId,
					variantId: creator.selectedVariantId ?? undefined,
					configuration: normalizedConfiguration,
				},
				listenerApi.signal
			);

			if (result.mode === 'quote') {
				listenerApi.dispatch(creatorActions.setPricingQuoteRequired(true));
				return;
			}

			listenerApi.dispatch(creatorActions.setPricingQuoteRequired(false));
			listenerApi.dispatch(creatorActions.setPricingBreakdown(result.breakdown));
		} catch (error) {
			if ((error as DOMException)?.name === 'AbortError') return;

			if (import.meta.env.DEV) console.error('Pricing error', error);
			listenerApi.dispatch(creatorActions.setPricingError('Nie udało się obliczyć ceny'));
		}
}
});
